import type { AdjustPicklistItemRequest, PicklistItemGroupDto } from '../types';
import { PicklistAdjustmentReason } from '../types';
import { isSerialGroup, mrsLineLabel } from './adjustModel';
import { qty, unitWord } from './picklistProgress';

/**
 * PT-6b — the Move dialog's pure layer. A Move is its own action, not an adjustment: the same units stay picked in the
 * same bin, and only the MRS line they count for changes. It asks no reason (the request carries the fixed
 * `WrongMrs`) and saves as one request the moment it is confirmed. No React, no fetch; `moveModel.test.ts` pins it.
 */

/** The row a Move starts from: a line and its own slot in one display bin. */
export interface MoveSource {
  lineId: number;
  slotId: number;
}

export interface MoveForm {
  /** The target line; null until one is chosen. */
  to: number | null;
  /** The quantity as typed; undefined until the person types (the default then shows). */
  n?: string;
}

export interface MoveTargetOption {
  lineId: number;
  label: string;
  disabled: boolean;
}

export interface MoveModel {
  code: string;
  bin: string;
  serial: string | null;
  isSerial: boolean;
  unit: string;
  fromLabel: string;
  /** "7 PIECE can move", or with the posted part named. */
  availableLabel: string;
  targets: MoveTargetOption[];
  to: number | null;
  quantityText: string;
  max: number;
  quantityInvalid: boolean;
  note: string;
  noteIsError: boolean;
  canMove: boolean;
  /** Why Move is disabled; empty when it is not. */
  moveTip: string;
  /** What the confirmed Move sends, less the version and the note; null until it can be sent. */
  move: AdjustPicklistItemRequest['moves'][number] | null;
  /** The toast after it saves. */
  doneText: string;
}

/** Each line's room: its plan less what is picked for it, across every bin. */
function roomByLine(group: PicklistItemGroupDto): Map<number, number> {
  const picked = new Map<number, number>();
  for (const display of group.bins ?? []) {
    for (const e of display.lines ?? []) {
      picked.set(e.picklistItemId, (picked.get(e.picklistItemId) ?? 0) + e.currentlyPickedQuantity);
    }
  }
  return new Map((group.plannedLines ?? []).map((l) => [l.id, Math.max(0, l.quantityPlanned - (picked.get(l.id) ?? 0))] as const));
}

/** The source row's own figures, or null when that line no longer holds a slot there (the picklist changed). */
function findSource(group: PicklistItemGroupDto, source: MoveSource) {
  for (const display of group.bins ?? []) {
    const entry = (display.lines ?? []).find(
      (e) => e.picklistItemId === source.lineId && e.picklistItemBinId === source.slotId,
    );
    if (entry) return { display, entry };
  }
  return null;
}

/** How many units of the source row may move: the picked ones not yet posted. */
export function movableUnits(group: PicklistItemGroupDto, source: MoveSource): number {
  const found = findSource(group, source);
  return found ? Math.max(0, found.entry.currentlyPickedQuantity - found.entry.postedQuantity) : 0;
}

/** The other lines that can still take a unit. */
export function moveTargetsWithRoom(group: PicklistItemGroupDto, fromLineId: number): number[] {
  const room = roomByLine(group);
  return (group.plannedLines ?? []).filter((l) => l.id !== fromLineId && (room.get(l.id) ?? 0) > 0).map((l) => l.id);
}

/** The target to preselect: the one line with room, else none. */
export function defaultMoveTarget(group: PicklistItemGroupDto, source: MoveSource): number | null {
  const targets = moveTargetsWithRoom(group, source.lineId);
  return targets.length === 1 ? targets[0] : null;
}

export function buildMoveModel(group: PicklistItemGroupDto, source: MoveSource, form: MoveForm): MoveModel | null {
  const found = findSource(group, source);
  const lines = group.plannedLines ?? [];
  const from = lines.find((l) => l.id === source.lineId);
  if (!found || !from) return null;

  const unit = group.unitOfMeasure ?? '';
  const uw = (n: number) => unitWord(unit, n);
  const isSerial = isSerialGroup(group);
  const serial = isSerial ? found.display.inventoryNumber ?? null : null;
  const room = roomByLine(group);
  const label = (id: number) => {
    const line = lines.find((l) => l.id === id);
    return line ? mrsLineLabel(line, lines) : '';
  };

  const posted = found.entry.postedQuantity;
  const unposted = Math.max(0, found.entry.currentlyPickedQuantity - posted);
  const targets: MoveTargetOption[] = lines
    .filter((l) => l.id !== source.lineId)
    .map((l) => {
      const rm = room.get(l.id) ?? 0;
      return {
        lineId: l.id,
        label: `${label(l.id)}${rm > 0 ? ` · room for ${qty(rm)} ${uw(rm)}` : ' · no room left'}`,
        disabled: rm <= 0,
      };
    });

  // A target that lost its room (the figures changed under the dialog) reads as not chosen.
  const to = form.to != null && targets.some((t) => t.lineId === form.to && !t.disabled) ? form.to : null;
  const toRoom = to != null ? room.get(to) ?? 0 : 0;
  const toPlan = to != null ? lines.find((l) => l.id === to)?.quantityPlanned ?? 0 : 0;
  const max = isSerial ? Math.min(1, unposted) : to != null ? Math.min(unposted, toRoom) : unposted;
  const quantityText = isSerial ? '1' : form.n ?? String(max);
  const n = quantityText.trim() === '' ? Number.NaN : Number(quantityText);
  const finite = Number.isFinite(n);
  const overRoom = to != null && finite && n > toRoom;
  const overHere = to != null && !overRoom && finite && n > unposted;
  const quantityInvalid = to != null && (!finite || n <= 0 || n > max);

  const fromLabel = label(source.lineId);
  const subject = isSerial ? serial ?? 'This serial' : `${qty(n)} ${uw(n)}`;
  const note = unposted <= 0
    ? 'Nothing here can move — it is already posted.'
    : targets.every((t) => t.disabled)
      ? 'No other MRS line of this item has room for more.'
      : to == null
        ? 'Choose the MRS line these units are really for.'
        : overRoom
          ? `Only ${qty(toRoom)} ${uw(toRoom)} can move — ${label(to)} is planned for ${qty(toPlan)} and has room for ${qty(toRoom)} more.`
          : overHere
            ? `Only ${qty(unposted)} ${uw(unposted)} here can move — the rest is already posted.`
            : quantityInvalid
              ? 'Enter how many to move.'
              : `${subject} will count for ${label(to)} instead of ${fromLabel}.`;

  const blocked = unposted <= 0 || targets.every((t) => t.disabled);
  const canMove = !blocked && to != null && !quantityInvalid;
  return {
    code: group.itemCode,
    bin: found.display.binNumber,
    serial,
    isSerial,
    unit,
    fromLabel,
    availableLabel: `${qty(unposted)} ${uw(unposted)} can move${posted > 0 ? ` · ${qty(posted)} already posted` : ''}`,
    targets,
    to,
    quantityText,
    max,
    quantityInvalid,
    note,
    noteIsError: blocked || overRoom || overHere || quantityInvalid,
    canMove,
    moveTip: canMove ? '' : blocked ? note : to == null ? 'Choose an MRS line first' : 'Fix the quantity first',
    move: canMove && to != null
      ? { fromPicklistItemId: source.lineId, picklistItemBinId: source.slotId, toPicklistItemId: to, quantity: n }
      : null,
    doneText: canMove && to != null ? `Moved ${isSerial ? serial ?? '1 unit' : `${qty(n)} ${uw(n)}`} to ${label(to)}` : '',
  };
}

/** The Move's request: one move, the fixed reason, an optional note. Null without a version or a sendable move. */
export function buildMoveRequest(
  model: MoveModel,
  notes: string,
  version: string | null | undefined,
): AdjustPicklistItemRequest | null {
  if (!model.move || !version) return null;
  const note = notes.trim();
  return {
    version,
    reason: PicklistAdjustmentReason.WrongMrs,
    notes: note ? note : null,
    rows: [],
    moves: [model.move],
    dispositions: [],
  };
}
